import {
  Grid,
  Input,
  Typography,
  Box,
  LoadingButton,
} from '@oresundsbron/bridge-ui';
import { useTranslation } from 'next-i18next';
import { FC, useMemo } from 'react';
import { pipe } from 'fp-ts/lib/function';
import { findFirst, map as mapA } from 'fp-ts/lib/Array';
import { getOrElse, map } from 'fp-ts/lib/Option';
import { toCurrency } from '../../../lib/currency';
import {
  ContractTranslationKey,
  getContractTranslationKey,
  TranslateableContracts,
} from '../../../lib/contract-translations';
import { ContractName } from '../../../components/ContractName';
import { useForm } from '@oresundsbron/use-form';
import { ThirdPartySignup } from './types';
import { useContracts } from '../../../hooks/useContracts';
import { NavisionContractType } from '@oresundsbron/validators';
import { Currency } from '@oresundsbron/type-safeguards';
import { ContractSection } from '@oresundsbron/api';
import { TermsOfAgreementText } from '../../../components/TermsOfAgreementText';
import { useRouter } from 'next/router';

interface TermsOfAgreementProps {
  namespace: string;
  isLoading: boolean;
  disabled: boolean;
  section: ContractSection;
  contractType: NavisionContractType;
}

const TermsOfAgreement: FC<TermsOfAgreementProps> = ({
  namespace,
  isLoading,
  disabled,
  section,
  contractType,
}) => {
  const { locale } = useRouter();
  const { t } = useTranslation(['privateSignUp', 'common'], {
    nsMode: 'fallback',
  });

  const { contracts, main } = useContracts(section, contractType);

  const displayCurrency = useMemo(() => Currency.DKK, []);

  const { registerField, getError } = useForm<ThirdPartySignup>({
    namespace,
  });

  const c = useMemo(
    () =>
      pipe(
        contracts,
        mapA((c) =>
          pipe(
            c.value as TranslateableContracts,
            getContractTranslationKey,
            getOrElse(() => ''),
            (k) => ({
              value: t('price.yearly', {
                price: pipe(
                  c.prices || [],
                  findFirst((x) => x.currency === displayCurrency),
                  map((p) => toCurrency(p.price || 0, locale, displayCurrency)),
                  getOrElse(() => '')
                ),
              }),
              label: k,
              discount: false,
            })
          )
        )
      ),
    [contracts, t, displayCurrency, locale]
  );

  return (
    <Grid>
      <Box className="col-span-full mb-6 mt-8">
        <Typography intent="title" className="mb-3">
          {t('form.sections.termsOfAgreement.subTitle')}
        </Typography>
        <Typography intent="label" size="sm">
          {t('form.labels.contract')}
        </Typography>
        <Box as="ul">
          {c.map(({ label, value }, i) => (
            <Box key={i} as="li" className="my-3 flex flex-row justify-between">
              <Typography>
                <ContractName contract={label as ContractTranslationKey} />
              </Typography>
              <Typography>{value}</Typography>
            </Box>
          ))}
          <Box
            as="li"
            className="flex flex-row justify-between border-t-2 border-primary-600 py-3"
          >
            <Typography>{t('signup.total')}</Typography>
            <Typography>
              {t('price.yearly', {
                price: pipe(
                  main.prices || [],
                  findFirst((x) => x.currency === displayCurrency),
                  map((p) => toCurrency(p.price || 0, locale, displayCurrency)),
                  getOrElse(() => '')
                ),
              })}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Input
        {...registerField('termsOfAgreement.agreement', {
          type: 'checkbox',
          value: 'true',
        })}
        className="col-span-full"
        label={t('form.labels.termsOfAgreement')}
        error={getError('termsOfAgreement.agreement')}
        disabled={disabled}
        required
      />
      <Typography size="sm" className="col-span-full">
        <TermsOfAgreementText
          type={section === 'business' ? 'Oresund_Business' : 'OresundGO'}
          isOresundPay={false}
        />
      </Typography>
      <Box className="col-span-full justify-self-end">
        <LoadingButton
          intent="solid"
          type="submit"
          isLoading={isLoading}
          loadingLabel={t('action.loading')}
          disabled={disabled}
        >
          {t('action.pay')}
        </LoadingButton>
      </Box>
    </Grid>
  );
};

export default TermsOfAgreement;
